import { Component, inject, OnInit } from '@angular/core';
import { AuthProvider, AuthProviderForm, SessionsService } from '../../services/sessions.service';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ListboxModule } from 'primeng/listbox';
import { Checkbox } from 'primeng/checkbox';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CardModule } from 'primeng/card';
import { firstValueFrom } from 'rxjs';
import { MessageModule } from 'primeng/message';

export enum SessionSignInFlag {
  SignInRequired = 'signin-required',
  SessionRequired = 'session-required',
}

@Component({
  selector: 'app-besharp-session-signin',
  imports: [
    Button,
    Checkbox,
    ReactiveFormsModule,
    DividerModule,
    FloatLabelModule,
    InputTextModule,
    ListboxModule,
    InputGroupModule,
    InputGroupAddonModule,
    CardModule,
    FormsModule,
    RouterModule,
    MessageModule,
  ],
  providers: [],
  templateUrl: './session-signin.component.html',
  styleUrl: './session-signin.component.scss',
})
export class SessionSigninComponent implements OnInit {
  protected eSessionSignInFlag = SessionSignInFlag;
  protected authProviders: AuthProvider[] = [];
  protected selectedAuthProvider: AuthProvider | null = null;
  protected signInFormGroup: FormGroup = new FormGroup({});
  protected signInForm: AuthProviderForm | null = null;
  protected signingIn: boolean = false;
  protected errorMessage: string | null = null;
  protected sessionsService: SessionsService = inject(SessionsService);
  protected router: Router = inject(Router);
  protected route: ActivatedRoute = inject(ActivatedRoute);

  protected flagLabelMapping = {
    [SessionSignInFlag.SessionRequired]: 'No active session, please sign-in.',
    [SessionSignInFlag.SignInRequired]: 'Session expired, please sign-in again.',
  };

  protected formGroup: FormGroup = new FormGroup({
    name: new FormControl(''),
  });

  protected loading: boolean = false;
  protected saveCredentials: boolean = false;
  protected flags: SessionSignInFlag[] = [];

  public constructor() {}

  async ngOnInit(): Promise<void> {
    this.authProviders = this.sessionsService.getAuthenticationProviders();

    const queryParams = await firstValueFrom(this.route.queryParamMap);
    this.flags = Object.values(SessionSignInFlag).filter((flag) =>
      queryParams.get('flags')?.split(/(,|;)/).includes(flag)
    );
    this.selectAuthProvider(this.authProviders[0]);

    let sessionToPrefill = queryParams.get('prefill_session');
    if (sessionToPrefill) {
      const session = this.sessionsService.getSession(sessionToPrefill);
      const authProvider = this.sessionsService.getAuthProvider(session.authProvider);

      if (session.prefillInfo) {
        for (const param of authProvider.getSignInFields()) {
          if (param.key in session.prefillInfo) {
            this.formGroup.controls[param.key].setValue(session.prefillInfo[param.key]);
          }
        }

        for (const [key, value] of Object.entries(session.authCredentials ?? {})) {
          this.formGroup.controls[key].setValue(value);
        }
      }

      this.formGroup.updateValueAndValidity();
    }
  }

  public selectAuthProvider(authProvider: AuthProvider) {
    this.selectedAuthProvider = authProvider;
    this.errorMessage = null;

    const formControls: { [key: string]: FormControl } = {};

    for (const name of Object.keys(this.formGroup.controls)) {
      this.formGroup.removeControl(name);
    }

    for (const control of authProvider.getSignInFields()) {
      switch (control.type) {
        case 'text':
          formControls[control.key] = new FormControl<string>('', Validators.required);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
        case 'email':
          formControls[control.key] = new FormControl<string>('', [Validators.email, Validators.required]);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
        case 'checkbox':
          formControls[control.key] = new FormControl<boolean>(false);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
        case 'password':
          formControls[control.key] = new FormControl<string>('', Validators.required);
          this.formGroup.addControl(control.key, formControls[control.key]);
          break;
      }
    }

    this.formGroup.updateValueAndValidity();

    this.signInForm = authProvider.getSignInFields();
  }

  protected isValid(): boolean {
    return this.formGroup.valid;
  }

  protected onChangeSaveCredentials(checked: boolean) {
    this.saveCredentials = checked;
  }

  protected async startSession(event: any) {
    console.debug(event);

    this.errorMessage = null;

    if (!this.selectedAuthProvider) {
      return;
    }

    const authData = this.formGroup.getRawValue();

    for (const key of Object.keys(authData)) {
      if (typeof authData[key] === 'string') {
        if (key === 'username') {
          authData[key] = authData[key].toLowerCase().trim();
        } else {
          authData[key] = authData[key].trim();
        }
      }
    }

    this.loading = true;
    this.sessionsService
      .startSession(this.selectedAuthProvider?.name, authData, {
        saveCredentials: this.saveCredentials,
        setActive: true,
      })
      .then(() => {})
      .catch((err) => {
        console.error('Authentication error:', err);

        if (err.challenge) {
          switch (err.challenge) {
            case 'NEW_PASSWORD_REQUIRED':
              this.router.navigate(['/session/new-password-required'], {
                queryParams: {
                  username: err.username || authData.username,
                  provider: this.selectedAuthProvider?.name,
                },
              });
              return;

            default:
              this.errorMessage = err.message || `Authentication challenge: ${err.challenge}`;
              break;
          }
        } else {
          this.errorMessage = err.message || err.toString();
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }
}
